<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <!-- 헤더 -->
        <div class="nano-header mb-4">
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper">
              <i class="las la-magic"></i>
            </div>
            <div class="ml-3">
              <h3 class="header-title mb-1">나노바나나 AI 이미지 에디터</h3>
              <p class="header-subtitle mb-0">AI를 활용하여 이미지를 쉽고 빠르게 생성하고 편집해보세요</p>
            </div>
          </div>
          <div v-if="!hasGeminiApiKey" class="alert alert-warning mt-3 mb-0">
            <i class="las la-exclamation-triangle mr-2"></i>
            <strong>Gemini API 키가 필요합니다!</strong>
            우측 상단 프로필 메뉴에서 "Gemini API 설정"을 클릭하여 API 키를 먼저 설정해주세요.
          </div>
        </div>

        <div class="row">
          <!-- 좌측 컨트롤 패널 -->
          <div class="col-lg-5">
            <div class="card nano-control-card shadow-sm">
              <div class="card-header nano-card-header">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <i class="las la-sliders-h mr-2 text-primary"></i>
                    <h5 class="mb-0 font-weight-bold">AI 이미지 도구</h5>
                  </div>
                  <!-- 실행 버튼들 -->
                  <div class="nano-header-buttons">
                    <button
                      @click="handleClear"
                      class="btn btn-outline-secondary btn-sm rounded-pill"
                      type="button"
                    >
                      <i class="las la-redo mr-1"></i>초기화
                    </button>
                    <button
                      @click="handleProcess"
                      :disabled="isProcessDisabled"
                      class="btn btn-primary btn-sm rounded-pill nano-main-btn"
                      type="button"
                    >
                      <span v-if="isLoading || isAnalyzing">
                        <b-spinner small class="mr-2"></b-spinner>{{ isAnalyzing ? '분석 중...' : (hasValidImage ? '편집' : '생성') + ' 중...' }}
                      </span>
                      <span v-else>
                        <i class="las la-magic mr-2"></i>AI {{ hasValidImage ? '편집 시작' : '이미지 생성' }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="card-body p-4 nano-scrollable-content">
                <!-- 1. 프롬프트 입력 -->
                <div class="nano-section mb-4">
                  <div class="section-header d-flex justify-content-between align-items-center mb-3">
                    <div class="section-title">
                      <span class="section-number">1</span>
                      <span class="ml-2">프롬프트 입력</span>
                    </div>
                    <div class="d-flex align-items-center">
                      <div class="custom-control custom-switch mr-3">
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          id="multipleImageSwitch"
                          v-model="isMultipleImageMode"
                        />
                        <label class="custom-control-label" for="multipleImageSwitch">
                          <small class="font-weight-bold">여러장 생성</small>
                        </label>
                      </div>
                      <div v-if="isMultipleImageMode" class="concept-count-input">
                        <input
                          type="number"
                          v-model.number="conceptCount"
                          class="form-control form-control-sm"
                          min="2"
                          max="10"
                        />
                        <small class="text-muted ml-1">개</small>
                      </div>
                    </div>
                  </div>
                  <textarea
                    v-model="prompt"
                    class="form-control nano-textarea"
                    rows="4"
                    :placeholder="promptPlaceholder"
                    @paste="handlePromptPaste"
                    @keydown="handlePromptKeydown"
                  ></textarea>
                  <div class="nano-hint mt-2">
                    <i class="las la-info-circle mr-1"></i>
                    <small>{{ isMultipleImageMode ? `AI가 프롬프트를 분석하여 ${conceptCount}개의 컨셉 이미지를 생성합니다.` : 'Ctrl+Enter로 실행, Enter로 줄바꿈' }}</small>
                  </div>
                </div>

                

                <!-- 이미지 입력 섹션 -->
                <div class="nano-section mb-4">
                  <div class="section-header mb-3">
                    <div class="section-title">
                      <span class="section-number">2</span>
                      <span class="ml-2">이미지 선택 <span class="text-muted small">(편집 시 필요)</span></span>
                    </div>
                  </div>

                  <!-- 입력 방법 선택 -->
                  <div class="input-method-tabs mb-3">
                    <div class="nano-tabs">
                      <button
                        @click="setInputMethod('upload')"
                        :class="['nano-tab', inputMethod === 'upload' ? 'active' : '']"
                        type="button"
                      >
                        <i class="las la-upload"></i>
                        <span>파일 업로드</span>
                      </button>
                      <button
                        @click="setInputMethod('url')"
                        :class="['nano-tab', inputMethod === 'url' ? 'active' : '']"
                        type="button"
                      >
                        <i class="las la-link"></i>
                        <span>URL 입력</span>
                      </button>
                      <button
                        @click="setInputMethod('result')"
                        :class="['nano-tab', inputMethod === 'result' ? 'active' : '']"
                        :disabled="!editedImage"
                        type="button"
                      >
                        <i class="las la-redo"></i>
                        <span>결과 재편집</span>
                      </button>
                      <button
                        @click="setInputMethod('paste')"
                        :class="['nano-tab', inputMethod === 'paste' ? 'active' : '']"
                        type="button"
                      >
                        <i class="las la-paste"></i>
                        <span>붙여넣기</span>
                      </button>
                    </div>
                  </div>

                  <!-- 파일 업로드 -->
                  <div v-if="inputMethod === 'upload'" class="custom-file-upload" @click="$refs.fileInput.click()">
                    <div v-if="originalImages.length === 0" class="upload-placeholder">
                      <i class="las la-cloud-upload-alt" style="font-size: 3rem; color: #6c757d;"></i>
                      <p class="mb-2"><strong>클릭하여 이미지 업로드</strong></p>
                      <p class="text-muted small">PNG, JPG, WEBP 등 (다중 선택 가능)</p>
                    </div>
                    <div v-else class="uploaded-images">
                      <div class="image-grid">
                        <div v-for="(image, index) in originalImages" :key="index" class="image-item">
                          <img :src="image" alt="업로드된 이미지" class="img-fluid rounded" />
                          <button @click.stop="removeImage(index)" class="btn btn-sm btn-danger remove-btn">
                            <i class="las la-times"></i>
                          </button>
                        </div>
                      </div>
                      <p class="text-muted small mt-2">{{ originalImages.length }}개 이미지 선택됨</p>
                    </div>
                    <input
                      ref="fileInput"
                      type="file"
                      :key="fileInputKey"
                      accept="image/*"
                      multiple
                      @change="handleFileChange"
                      style="display: none;"
                    />
                  </div>

                  <!-- URL 입력 -->
                  <div v-if="inputMethod === 'url'">
                    <div class="nano-url-input-group">
                      <textarea
                        v-model="imageUrl"
                        class="form-control nano-textarea"
                        placeholder="이미지 URL을 입력하세요 (여러 개인 경우 줄바꿈으로 구분)"
                        rows="3"
                        @blur="loadImageFromUrl"
                      ></textarea>
                      <button @click="loadImageFromUrl" class="btn btn-primary nano-url-load-btn" type="button">
                        <i class="las la-download"></i>
                      </button>
                    </div>
                    <div v-if="originalImages.length > 0 && inputMethod === 'url'" class="mt-3">
                      <div class="image-grid">
                        <div v-for="(image, index) in originalImages" :key="index" class="image-item">
                          <img :src="image" alt="URL 이미지" class="img-fluid rounded" style="max-height: 200px;" />
                          <button @click.stop="removeImage(index)" class="btn btn-sm btn-danger remove-btn">
                            <i class="las la-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 결과 재편집 -->
                  <div v-if="inputMethod === 'result' && editedImage">
                    <div class="nano-result-reuse">
                      <div class="reuse-image-wrapper">
                        <img :src="editedImage.imageUrl" alt="재편집할 이미지" class="img-fluid rounded" />
                      </div>
                      <div class="reuse-info">
                        <i class="las la-info-circle mr-1"></i>
                        <span>이전 결과를 다시 편집합니다</span>
                      </div>
                    </div>
                  </div>

                  <!-- 클립보드 붙여넣기 -->
                  <div v-if="inputMethod === 'paste'">
                    <div
                      class="paste-area"
                      @click="focusPasteArea"
                      @paste="handlePaste"
                      @dragover.prevent
                      @drop.prevent="handleDrop"
                      tabindex="0"
                      ref="pasteArea"
                    >
                      <div v-if="originalImages.length === 0" class="paste-placeholder">
                        <i class="las la-paste" style="font-size: 3rem; color: #6c757d;"></i>
                        <p class="mb-2"><strong>이미지를 붙여넣기 하세요</strong></p>
                        <p class="text-muted small">Ctrl+V 또는 이 영역을 클릭 후 붙여넣기</p>
                        <p class="text-muted small">또는 이미지를 드래그해서 놓으세요 (다중 선택 가능)</p>
                      </div>
                      <div v-else class="pasted-images">
                        <div class="image-grid">
                          <div v-for="(image, index) in originalImages" :key="index" class="image-item">
                            <img :src="image" alt="붙여넣은 이미지" class="img-fluid rounded" />
                            <button @click.stop="removeImage(index)" class="btn btn-sm btn-danger remove-btn">
                              <i class="las la-times"></i>
                            </button>
                          </div>
                        </div>
                        <p class="text-muted small mt-2">{{ originalImages.length }}개 이미지 선택됨</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 모델 및 설정 -->
                <div class="nano-section mb-4">
                  <div class="section-header mb-3">
                    <div class="section-title">
                      <span class="section-number">3</span>
                      <span class="ml-2">모델 및 설정</span>
                    </div>
                  </div>
                  <div class="model-settings-grid">
                    <!-- 모델 선택 -->
                    <div class="setting-item">
                      <select v-model="selectedModel" class="form-control nano-select">
                        <option value="gemini-2.5-flash-image">🚀 Nano Banana (빠름)</option>
                        <option value="gemini-3-pro-image-preview">⭐ Nano Banana Pro (고품질)</option>
                      </select>
                    </div>

                    <!-- 종횡비 선택 (Pro 모델만) -->
                    <div class="d-flex justify-content-between" v-if="selectedModel === 'gemini-3-pro-image-preview'">
                      <div class="w-50">
                        <label class="setting-label">
                          <i class="las la-expand-arrows-alt mr-1"></i>종횡비
                        </label>
                        <select v-model="aspectRatio" class="form-control nano-select">
                          <option value="">🔄 자동 (원본 비율)</option>
                          <option value="1:1">⬜ 1:1 (정사각형)</option>
                          <option value="16:9">▭ 16:9 (와이드)</option>
                          <option value="9:16">▯ 9:16 (세로)</option>
                          <option value="21:9">▭ 21:9 (울트라와이드)</option>
                          <option value="4:3">▭ 4:3 (가로)</option>
                          <option value="3:4">▯ 3:4 (세로)</option>
                          <option value="3:2">▭ 3:2 (가로)</option>
                          <option value="2:3">▯ 2:3 (세로)</option>
                          <option value="5:4">▭ 5:4 (가로)</option>
                          <option value="4:5">▯ 4:5 (세로)</option>
                        </select>
                      </div>
                      <div class="w-50 ml-2">
                        <label class="setting-label">
                          <i class="las la-image mr-1"></i>해상도
                        </label>
                        <select v-model="imageSize" class="form-control nano-select">
                          <option value="1K">1K (빠름)</option>
                          <option value="2K">2K (균형)</option>
                          <option value="4K">4K (고품질)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="nano-hint mt-2">
                    <i class="las la-info-circle mr-1"></i>
                    <small v-if="selectedModel === 'gemini-2.5-flash-image'">
                      Nano Banana는 1024px 고정 해상도로 빠르게 생성됩니다.
                    </small>
                    <small v-else>
                      Nano Banana Pro는 고품질 이미지를 생성하며, 해상도와 종횡비를 선택할 수 있습니다.
                    </small>
                  </div>
                </div>

                <!-- 프리셋 버튼들 -->
                <div class="nano-section mb-4">
                  <div class="section-header d-flex justify-content-between align-items-center mb-3">
                    <div class="section-title">
                      <span class="section-number">4</span>
                      <span class="ml-2">빠른 {{ isMultipleImageMode ? '여러장 생성' : (hasValidImage ? '편집' : '생성') }} 프리셋</span>
                    </div>
                    <button
                      @click="openAddPresetModal"
                      class="btn btn-sm btn-outline-primary rounded-pill"
                      type="button"
                      title="프리셋 추가"
                    >
                      <i class="las la-plus mr-1"></i>추가
                    </button>
                  </div>
                  <div class="preset-buttons">
                    <!-- 사용자 정의 프리셋 -->
                    <div v-if="userPresets.length > 0" class="mb-3">
                      <div class="preset-category-label mb-2">
                        <i class="las la-user mr-1"></i>내 프리셋
                      </div>
                      <div class="preset-grid">
                        <div v-for="preset in userPresets" :key="'user-' + preset.id" class="preset-item-wrapper">
                          <button
                            @click="applyPreset(preset)"
                            class="nano-preset-btn user-preset"
                            :title="preset.description"
                          >
                            <span><i :class="preset.icon"></i>{{ preset.name }}</span>
                            
                          </button>
                          <button
                            @click="deleteUserPreset(preset.id)"
                            class="preset-delete-btn"
                            title="삭제"
                          >
                            <i class="las la-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- 기본 프리셋 -->
                    <div>
                      <div class="preset-category-label mb-2">
                        <i class="las la-star mr-1"></i>기본 프리셋
                      </div>
                      <div class="preset-grid">
                        <div v-for="preset in currentPresets" :key="'default-' + preset.id">
                          <button
                            @click="applyPreset(preset)"
                            class="nano-preset-btn default-preset"
                            :title="preset.description"
                          >
                            <span><i :class="preset.icon"></i>{{ preset.name }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            </div>
          </div>

          <!-- 우측 결과 패널 -->
          <div class="col-lg-7">
            <div class="card nano-result-card shadow-sm">
              <div class="card-header nano-card-header">
                <div class="d-flex align-items-center">
                  <i :class="isMultipleImageMode ? 'las la-project-diagram' : 'las la-image'" class="mr-2 text-primary"></i>
                  <h5 class="mb-0 font-weight-bold">{{ isMultipleImageMode ? '이미지 컨셉' : '편집 결과' }}</h5>
                </div>
              </div>
              <div class="card-body p-4 nano-scrollable-content">
                <!-- 에러 메시지 -->
                <div v-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
                  <i class="las la-exclamation-triangle mr-2"></i>
                  <div>
                    <strong>오류:</strong> {{ error }}
                  </div>
                </div>

                <!-- 에이전틱 모드: 컨셉 트리 -->
                <div v-if="isMultipleImageMode">
                  <!-- 프롬프트 분석 중 -->
                  <div v-if="isAnalyzing" class="text-center py-5">
                    <b-spinner style="width: 3rem; height: 3rem;"></b-spinner>
                    <p class="mt-3 mb-0">프롬프트를 분석하고 컨셉을 생성하고 있습니다...</p>
                  </div>

                  <!-- 컨셉 목록 -->
                  <div v-else-if="concepts.length > 0" class="concepts-tree">
                    <div v-for="(concept, index) in concepts" :key="concept.id" class="concept-item mb-3">
                      <div class="concept-card card shadow-sm">
                        <div class="card-body p-3">
                          <!-- 컨셉 헤더 -->
                          <div class="concept-header d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                              <div class="concept-number">{{ index + 1 }}</div>
                              <h6 class="mb-0 ml-2 font-weight-bold">컨셉 {{ index + 1 }}</h6>
                            </div>
                            <div class="d-flex align-items-center">
                              <button
                                v-if="!concept.imageUrl && !concept.isGenerating"
                                @click="generateConceptImage(concept)"
                                class="btn btn-sm btn-primary rounded-pill mr-2"
                              >
                                <i :class="concept.sourceImageData ? 'las la-edit' : 'las la-magic'" class="mr-1"></i>
                                {{ concept.sourceImageData ? '수정' : '생성' }}
                              </button>
                              <button
                                @click="removeConcept(index)"
                                class="btn btn-sm btn-danger rounded-circle concept-delete-btn"
                                title="컨셉 삭제"
                              >
                                <i class="las la-times"></i>
                              </button>
                            </div>
                          </div>

                          <!-- 좌우 레이아웃 -->
                          <div class="concept-content-layout">
                            <!-- 좌측: 프롬프트 및 설정 -->
                            <div class="concept-left">
                              <!-- 프롬프트 영역 -->
                          <div class="prompts-section">
                            <!-- 한글 프롬프트 -->
                            <div class="form-group mb-2">
                              <label class="prompt-label d-flex align-items-center justify-content-between">
                                <span><i class="las la-language mr-1"></i>한글 프롬프트</span>
                                <span v-if="concept.isTranslating" class="text-primary">
                                  <b-spinner small class="mr-1"></b-spinner>
                                  <small>번역 중...</small>
                                </span>
                              </label>
                              <textarea
                                v-model="concept.korean"
                                class="form-control prompt-input"
                                rows="2"
                                @focus="concept.koreanBeforeEdit = concept.korean"
                                @blur="onKoreanPromptEdit(concept)"
                                :disabled="concept.isGenerating || concept.isTranslating"
                                placeholder="한글로 이미지 설명을 입력하세요"
                              ></textarea>
                            </div>

                            <!-- 영문 프롬프트 -->
                            <div class="form-group mb-3">
                              <label class="prompt-label">
                                <i class="las la-globe mr-1"></i>영문 프롬프트
                              </label>
                              <textarea
                                v-model="concept.english"
                                class="form-control prompt-input english-prompt"
                                rows="2"
                                readonly
                                placeholder="자동 번역됩니다"
                              ></textarea>
                            </div>
                          </div>

                          <!-- 생성 옵션 (이미지가 없을 때) -->
                          <div v-if="!concept.imageUrl && !concept.isGenerating" class="generate-options-section mb-3">
                            <!-- 생성/수정 모드 선택 (원본 이미지가 있을 때만 표시) -->
                            <div v-if="concept.sourceImageData" class="generate-mode-options mb-2">
                              <div class="option-card" :class="{ active: concept.useSourceImage !== false }" @click="concept.useSourceImage = true">
                                <input type="radio" :id="`generate-edit-${concept.id}`" :name="`generate-mode-${concept.id}`" :value="true" v-model="concept.useSourceImage" class="d-none" />
                                <label :for="`generate-edit-${concept.id}`" class="mb-0 w-100 cursor-pointer">
                                  <i class="las la-edit mr-1"></i>
                                  <span class="font-weight-bold">수정</span>
                                  <small class="d-block text-muted mt-1">원본 이미지 기반 편집</small>
                                </label>
                              </div>
                              <div class="option-card" :class="{ active: concept.useSourceImage === false }" @click="concept.useSourceImage = false">
                                <input type="radio" :id="`generate-new-${concept.id}`" :name="`generate-mode-${concept.id}`" :value="false" v-model="concept.useSourceImage" class="d-none" />
                                <label :for="`generate-new-${concept.id}`" class="mb-0 w-100 cursor-pointer">
                                  <i class="las la-magic mr-1"></i>
                                  <span class="font-weight-bold">생성</span>
                                  <small class="d-block text-muted mt-1">프롬프트로 새로 생성</small>
                                </label>
                              </div>
                            </div>

                            <!-- 모델 및 해상도 설정 -->
                            <div class="generate-settings">
                              <div class="row">
                                <div class="col-6">
                                  <label class="setting-label">모델</label>
                                  <select v-model="concept.regenerateModel" class="form-control form-control-sm">
                                    <option value="gemini-2.5-flash-image">Nano Banana</option>
                                    <option value="gemini-3-pro-image-preview">Nano Banana Pro</option>
                                  </select>
                                </div>
                                <div class="col-6" v-if="concept.regenerateModel === 'gemini-3-pro-image-preview'">
                                  <label class="setting-label">해상도</label>
                                  <select v-model="concept.regenerateImageSize" class="form-control form-control-sm">
                                    <option value="1K">1K</option>
                                    <option value="2K">2K</option>
                                    <option value="4K">4K</option>
                                  </select>
                                </div>
                              </div>
                              <div class="row mt-2" v-if="concept.regenerateModel === 'gemini-3-pro-image-preview'">
                                <div class="col-12">
                                  <label class="setting-label">종횡비</label>
                                  <select v-model="concept.regenerateAspectRatio" class="form-control form-control-sm">
                                    <option value="">🔄 자동 (원본)</option>
                                    <option value="1:1">1:1 (정사각형)</option>
                                    <option value="16:9">16:9 (와이드)</option>
                                    <option value="9:16">9:16 (세로)</option>
                                    <option value="4:3">4:3 (가로)</option>
                                    <option value="3:4">3:4 (세로)</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- 재생성 옵션 (이미지가 있고 프롬프트가 수정된 경우) -->
                          <div v-if="concept.imageUrl && concept.promptModified && !concept.isGenerating" class="regenerate-section mb-3">
                            <div class="regenerate-header">
                              <i class="las la-exclamation-circle text-warning mr-1"></i>
                              <span class="font-weight-bold">프롬프트가 수정되었습니다</span>
                            </div>

                            <!-- 재생성 모드 선택 -->
                            <div class="regenerate-options mb-2">
                              <div class="option-card" :class="{ active: !concept.regenerateFromCurrent }" @click="concept.regenerateFromCurrent = false">
                                <input
                                  type="radio"
                                  :id="`regenerate-new-${concept.id}`"
                                  :name="`regenerate-mode-${concept.id}`"
                                  :value="false"
                                  v-model="concept.regenerateFromCurrent"
                                  class="d-none"
                                />
                                <label :for="`regenerate-new-${concept.id}`" class="mb-0 w-100 cursor-pointer">
                                  <i class="las la-plus-circle mr-1"></i>
                                  <span class="font-weight-bold">새로 생성</span>
                                  <small class="d-block text-muted mt-1">원본 이미지 기반</small>
                                </label>
                              </div>
                              <div class="option-card" :class="{ active: concept.regenerateFromCurrent }" @click="concept.regenerateFromCurrent = true">
                                <input
                                  type="radio"
                                  :id="`regenerate-edit-${concept.id}`"
                                  :name="`regenerate-mode-${concept.id}`"
                                  :value="true"
                                  v-model="concept.regenerateFromCurrent"
                                  class="d-none"
                                />
                                <label :for="`regenerate-edit-${concept.id}`" class="mb-0 w-100 cursor-pointer">
                                  <i class="las la-edit mr-1"></i>
                                  <span class="font-weight-bold">현재 이미지 편집</span>
                                  <small class="d-block text-muted mt-1">생성된 이미지 기반</small>
                                </label>
                              </div>
                            </div>

                            <!-- 모델 및 해상도 설정 -->
                            <div class="regenerate-settings mb-2">
                              <div class="row">
                                <div class="col-6">
                                  <label class="setting-label">모델</label>
                                  <select v-model="concept.regenerateModel" class="form-control form-control-sm">
                                    <option value="gemini-2.5-flash-image">Nano Banana</option>
                                    <option value="gemini-3-pro-image-preview">Nano Banana Pro</option>
                                  </select>
                                </div>
                                <div class="col-6" v-if="concept.regenerateModel === 'gemini-3-pro-image-preview'">
                                  <label class="setting-label">해상도</label>
                                  <select v-model="concept.regenerateImageSize" class="form-control form-control-sm">
                                    <option value="1K">1K</option>
                                    <option value="2K">2K</option>
                                    <option value="4K">4K</option>
                                  </select>
                                </div>
                              </div>
                              <div class="row mt-2" v-if="concept.regenerateModel === 'gemini-3-pro-image-preview'">
                                <div class="col-12">
                                  <label class="setting-label">종횡비</label>
                                  <select v-model="concept.regenerateAspectRatio" class="form-control form-control-sm">
                                    <option value="">🔄 자동 (원본)</option>
                                    <option value="1:1">1:1 (정사각형)</option>
                                    <option value="16:9">16:9 (와이드)</option>
                                    <option value="9:16">9:16 (세로)</option>
                                    <option value="21:9">21:9 (울트라와이드)</option>
                                    <option value="4:3">4:3 (가로)</option>
                                    <option value="3:4">3:4 (세로)</option>
                                    <option value="3:2">3:2 (가로)</option>
                                    <option value="2:3">2:3 (세로)</option>
                                    <option value="5:4">5:4 (가로)</option>
                                    <option value="4:5">4:5 (세로)</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            <!-- 재생성 버튼 -->
                            <button
                              @click="regenerateConceptImage(concept)"
                              class="btn btn-success btn-block rounded-pill"
                            >
                              <i class="las la-redo mr-1"></i>이미지 재생성
                            </button>
                          </div>
                            </div>

                            <!-- 우측: 생성된 이미지 -->
                            <div class="concept-right">
                              <!-- 생성 중 -->
                              <div v-if="concept.isGenerating" class="generating-overlay">
                                <div class="text-center">
                                  <b-spinner style="width: 2rem; height: 2rem;" variant="primary"></b-spinner>
                                  <p class="mt-2 mb-0 text-primary font-weight-bold">이미지 생성 중...</p>
                                </div>
                              </div>

                              <!-- 생성된 이미지 -->
                              <div v-else-if="concept.imageUrl" class="concept-image-wrapper">
                                <div class="concept-image-container position-relative">
                                  <img :src="concept.imageUrl" alt="생성된 이미지" class="img-fluid rounded" />
                                  <div class="image-overlay">
                                    <button
                                      @click="downloadConceptImage(concept, index)"
                                      class="btn btn-light btn-sm rounded-circle image-action-btn"
                                      title="이미지 다운로드"
                                    >
                                      <i class="las la-download"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <!-- 플레이스홀더 -->
                              <div v-else class="concept-image-placeholder">
                                <i class="las la-image"></i>
                                <p>이미지 생성 대기</p>
                              </div>

                              <!-- 에러 -->
                              <div v-if="concept.error" class="alert alert-danger alert-sm mt-2 mb-0">
                                <i class="las la-exclamation-triangle mr-1"></i>
                                <small>{{ concept.error }}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 모두 생성 및 프리셋 저장 버튼 -->
                    <div class="text-center mt-3">
                      <button
                        @click="generateAllConcepts"
                        :disabled="isGeneratingAll"
                        class="btn btn-success mr-2"
                      >
                        <span v-if="isGeneratingAll">
                          <b-spinner small class="mr-2"></b-spinner>모든 이미지 {{ hasSourceImages ? '수정' : '생성' }} 중...
                        </span>
                        <span v-else>
                          <i :class="hasSourceImages ? 'las la-edit' : 'las la-magic'" class="mr-1"></i>
                          모든 컨셉 이미지 {{ hasSourceImages ? '수정' : '생성' }}
                        </span>
                      </button>
                      <button
                        @click="saveAsPreset"
                        :disabled="concepts.length === 0"
                        class="btn btn-outline-primary"
                      >
                        <i class="las la-save mr-1"></i>프리셋으로 저장
                      </button>
                    </div>
                  </div>

                  <!-- 플레이스홀더 -->
                  <div v-else class="result-placeholder">
                    <i class="las la-project-diagram" style="font-size: 4rem; color: #6c757d;"></i>
                    <h5 class="text-muted">프롬프트를 입력하고 생성 버튼을 눌러주세요</h5>
                    <p class="text-muted small">AI가 여러 컨셉의 이미지를 생성합니다</p>
                  </div>
                </div>

                <!-- 일반 모드: 단일 이미지 결과 -->
                <div v-else class="result-container position-relative">
                  <!-- 로딩 오버레이 -->
                  <div v-if="isLoading" class="nano-loading-overlay">
                    <div class="text-center">
                      <b-spinner style="width: 3.5rem; height: 3.5rem;" variant="primary"></b-spinner>
                      <p class="mt-3 mb-0 font-weight-bold text-primary">이미지를 {{ hasValidImage ? '편집' : '생성' }}하고 있습니다...</p>
                      <small class="text-muted">잠시만 기다려주세요</small>
                    </div>
                  </div>

                  <!-- 편집된 이미지 -->
                  <div v-if="editedImage && !isLoading" class="nano-result-wrapper">
                    <div class="nano-image-container">
                      <img :src="editedImage.imageUrl" alt="편집된 이미지" class="nano-result-image" />
                      <div class="nano-image-overlay">
                        <button
                          @click="downloadImage"
                          class="btn btn-primary btn-lg rounded-circle nano-download-btn"
                          title="이미지 다운로드"
                        >
                          <i class="las la-download"></i>
                        </button>
                      </div>
                    </div>
                    <div v-if="editedImage.text" class="nano-result-info">
                      <i class="las la-info-circle mr-2"></i>
                      <em>"{{ editedImage.text }}"</em>
                    </div>
                  </div>

                  <!-- 플레이스홀더 -->
                  <div v-if="!editedImage && !isLoading" class="nano-result-placeholder">
                    <div class="placeholder-icon-wrapper">
                      <i class="las la-image"></i>
                    </div>
                    <h5 class="placeholder-title">{{ hasValidImage ? '편집된' : '생성된' }} 이미지가 여기에 표시됩니다</h5>
                    <p class="placeholder-subtitle">프롬프트를 입력하고 생성 버튼을 눌러주세요</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 프리셋 추가 모달 -->
    <div v-if="showAddPresetModal" class="modal fade show d-block nano-modal-backdrop" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content nano-modal">
          <div class="modal-header nano-modal-header">
            <h5 class="modal-title font-weight-bold">
              <i class="las la-plus-circle mr-2"></i>새 프리셋 추가
            </h5>
            <button @click="closeAddPresetModal" type="button" class="close">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body nano-modal-body">
            <div class="form-group">
              <label class="nano-modal-label">프리셋 이름</label>
              <input
                v-model="newPreset.name"
                type="text"
                class="form-control nano-modal-input"
                placeholder="예: 내 스타일"
                maxlength="20"
              />
            </div>
            <div class="form-group">
              <label class="nano-modal-label">아이콘 선택</label>
              <div class="nano-icon-selector">
                <button
                  v-for="iconOption in iconOptions"
                  :key="iconOption.class"
                  @click="selectIcon(iconOption.class)"
                  :class="['nano-icon-option', { 'active': newPreset.icon === iconOption.class }]"
                  type="button"
                  :title="iconOption.name"
                >
                  <i :class="iconOption.class"></i>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label class="nano-modal-label">설명</label>
              <input
                v-model="newPreset.description"
                type="text"
                class="form-control nano-modal-input"
                placeholder="프리셋에 대한 간단한 설명"
                maxlength="50"
              />
            </div>
            <div class="form-group">
              <label class="nano-modal-label">프롬프트</label>
              <textarea
                v-model="newPreset.prompt"
                class="form-control nano-modal-input"
                rows="4"
                placeholder="현재 입력된 프롬프트를 사용하거나 새로 작성하세요"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer nano-modal-footer">
            <button @click="closeAddPresetModal" type="button" class="btn btn-secondary rounded-pill px-4">취소</button>
            <button @click="saveUserPreset" type="button" class="btn btn-primary rounded-pill px-4" :disabled="!canSavePreset">
              <i class="las la-save mr-1"></i>저장
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { scopedDbName } from '@/services/scopedDB'
import confirmInPageMixin from '@/mixins/confirmInPageMixin'
export default {
  mixins: [confirmInPageMixin],
  name: 'NanoBanana',
  data() {
    return {
      inputMethod: 'upload', // 'upload', 'url', 'result', 'paste'
      prompt: '',
      originalImages: [], // 다중 이미지 지원
      originalImage: null, // 기존 호환성 유지
      editedImage: null,
      imageUrl: '',
      isLoading: false,
      isLoadingUrl: false, // URL 로딩 중복 호출 방지
      error: null,
      fileInputKey: Date.now(),

      // 모델 및 설정
      selectedModel: 'gemini-2.5-flash-image', // 기본값: Nano Banana
      aspectRatio: '', // 종횡비 (Pro 모델만) - 빈값: 자동(원본 비율)
      imageSize: '1K', // 해상도 (Pro 모델만)

      // 에이전틱 이미지 생성 관련
      isMultipleImageMode: false, // 여러장 생성 모드
      conceptCount: 3, // 생성할 컨셉 개수 (기본값: 3)
      concepts: [], // 생성된 컨셉들 [{id, korean, english, imageUrl, isGenerating, error}]
      isAnalyzing: false, // 프롬프트 분석 중
      isGeneratingAll: false, // 모든 컨셉 이미지 생성 중

      // 사용자 정의 프리셋 관련
      userPresets: [],
      showAddPresetModal: false,
      newPreset: {
        name: '',
        icon: 'las la-star',
        description: '',
        prompt: ''
      },
      iconOptions: [
        { class: 'las la-star', name: '별' },
        { class: 'las la-heart', name: '하트' },
        { class: 'las la-fire', name: '불꽃' },
        { class: 'las la-gem', name: '보석' },
        { class: 'las la-crown', name: '왕관' },
        { class: 'las la-magic', name: '마법' },
        { class: 'las la-palette', name: '팔레트' },
        { class: 'las la-brush', name: '브러시' },
        { class: 'las la-camera', name: '카메라' },
        { class: 'las la-image', name: '이미지' },
        { class: 'las la-eye', name: '눈' },
        { class: 'las la-lightbulb', name: '전구' },
        { class: 'las la-rocket', name: '로켓' },
        { class: 'las la-bolt', name: '번개' },
        { class: 'las la-sun', name: '태양' },
        { class: 'las la-moon', name: '달' },
        { class: 'las la-leaf', name: '잎' },
        { class: 'las la-flower', name: '꽃' },
        { class: 'las la-coffee', name: '커피' },
        { class: 'las la-gift', name: '선물' },
        { class: 'las la-thumbs-up', name: '좋아요' },
        { class: 'las la-smile', name: '웃음' },
        { class: 'las la-music', name: '음악' },
        { class: 'las la-gamepad', name: '게임' }
      ],
      editPresets: [
        {
          id: 1,
          name: '한국 여성 모델',
          icon: 'las la-user-friends',
          description: '상품을 착용한 한국 여성 모델로 변경',
          prompt: 'Replace the background with a beautiful Korean female model wearing this product. Professional fashion photography style, natural lighting, clean white studio background, model should be attractive and elegant, high-quality commercial photography'
        },
        {
          id: 2,
          name: '화이트 배경',
          icon: 'las la-square',
          description: '깔끔한 화이트 배경으로 변경',
          prompt: 'Change the background to a clean, pure white background. Professional product photography style, soft even lighting, no shadows, commercial quality'
        },
        {
          id: 3,
          name: '라이프스타일',
          icon: 'las la-home',
          description: '일상적인 라이프스타일 배경',
          prompt: 'Place this product in a natural lifestyle setting. Modern, cozy interior background, natural lighting, realistic everyday environment, Instagram-worthy aesthetic'
        },
        {
          id: 4,
          name: '럭셔리 배경',
          icon: 'las la-gem',
          description: '고급스러운 럭셔리 배경',
          prompt: 'Change the background to a luxury, premium setting. Elegant marble or silk texture, sophisticated lighting, high-end boutique atmosphere, premium brand aesthetic'
        },
        {
          id: 5,
          name: '야외 자연',
          icon: 'las la-tree',
          description: '자연스러운 야외 배경',
          prompt: 'Place this product in a beautiful outdoor natural setting. Soft natural sunlight, green nature background, fresh and organic feeling, lifestyle photography'
        },
        {
          id: 6,
          name: '카페 분위기',
          icon: 'las la-coffee',
          description: '감성적인 카페 배경',
          prompt: 'Place this product in a cozy cafe setting. Warm ambient lighting, wooden tables, coffee shop atmosphere, Instagram-style aesthetic, lifestyle photography'
        },
        {
          id: 7,
          name: '미니멀스튜디오',
          icon: 'las la-camera',
          description: '미니멀한 스튜디오 배경',
          prompt: 'Professional studio photography with minimal background. Soft gradient lighting, clean and modern aesthetic, commercial product photography style'
        },
        {
          id: 8,
          name: '패션 런웨이',
          icon: 'las la-walking',
          description: '패션쇼 런웨이 스타일',
          prompt: 'Transform into a fashion runway style image with a Korean female model. Professional fashion photography, dramatic lighting, runway atmosphere, high fashion aesthetic'
        },
        {
          id: 9,
          name: '빈티지 감성',
          icon: 'las la-clock',
          description: '빈티지 레트로 배경',
          prompt: 'Add a vintage, retro aesthetic background. Warm film photography style, nostalgic atmosphere, vintage textures and colors'
        },
        {
          id: 10,
          name: '도시 배경',
          icon: 'las la-city',
          description: '현대적인 도시 배경',
          prompt: 'Place this product in a modern urban city setting. Contemporary architecture background, natural daylight, urban lifestyle photography'
        },
        {
          id: 11,
          name: '해변 리조트',
          icon: 'las la-umbrella-beach',
          description: '해변 휴양지 배경',
          prompt: 'Place this product in a beautiful beach resort setting. Ocean view, tropical atmosphere, vacation lifestyle, natural sunlight'
        },
        {
          id: 12,
          name: '그라데이션',
          icon: 'las la-palette',
          description: '컬러 그라데이션 배경',
          prompt: 'Change the background to a beautiful color gradient. Soft pastel colors, modern aesthetic, Instagram-worthy background, professional product photography'
        }
      ],
      generatePresets: [
        {
          id: 1,
          name: '패션 모델',
          icon: 'las la-user-friends',
          description: '한국 여성 패션 모델 이미지 생성',
          prompt: 'Beautiful Korean female fashion model, professional studio photography, elegant pose, modern fashion outfit, clean white background, high-quality commercial photography, natural lighting'
        },
        {
          id: 2,
          name: '상품 목업',
          icon: 'las la-tshirt',
          description: '상품 목업 이미지 생성',
          prompt: 'Professional product mockup, clean white background, modern minimalist style, commercial photography, high-quality product shot, studio lighting'
        },
        {
          id: 3,
          name: '라이프스타일',
          icon: 'las la-home',
          description: '라이프스타일 컨셉 이미지',
          prompt: 'Lifestyle photography concept, modern interior setting, natural lighting, cozy atmosphere, Instagram-worthy aesthetic, Korean home decor style'
        },
        {
          id: 4,
          name: '브랜드 배경',
          icon: 'las la-palette',
          description: '브랜드용 배경 이미지',
          prompt: 'Modern brand background, gradient colors, minimalist design, professional aesthetic, suitable for product overlay, clean and elegant'
        },
        {
          id: 5,
          name: '카페 씬',
          icon: 'las la-coffee',
          description: '카페 분위기 이미지',
          prompt: 'Cozy cafe interior, warm lighting, wooden furniture, coffee shop atmosphere, Korean cafe style, lifestyle photography, Instagram aesthetic'
        },
      ],
      multipleGeneratePresets: [
        {
          id: 1,
          name: '품질/소재 강조',
          icon: 'las la-gem',
          description: '프리미엄 품질과 소재의 우수성 부각',
          category: 'quality',
          concepts: [
            {
              korean: '순백의 무한 배경, 완벽한 스튜디오 조명, 중앙 배치, 상품 전체가 선명하게 보이는 정면 촬영',
              english: 'Pure white infinite background, perfect studio lighting setup with key light and fill light, product centered, front view, sharp focus, professional e-commerce photography, no text, no graphics'
            },
            {
              korean: '극세밀 매크로 촬영, 소재의 질감과 결이 생생하게 드러나는 클로즈업, 얕은 심도',
              english: 'Extreme macro photography, material texture and grain in vivid detail, shallow depth of field, bokeh background, professional product detail shot, no text overlay'
            },
            {
              korean: '45도 각도, 렘브란트 조명 기법, 부드러운 그림자로 입체감 강조, 고급스러운 분위기',
              english: '45-degree angle, Rembrandt lighting technique, soft shadows creating depth, premium atmosphere, professional commercial photography, clean composition, no text'
            },
            {
              korean: '천연 대리석 표면 위, 측면 자연광, 미니멀한 구도, 네거티브 스페이스 활용',
              english: 'Natural marble surface, side natural lighting, minimalist composition, negative space utilization, luxury product photography, editorial style, no text elements'
            },
            {
              korean: '구조적 디테일 강조, 하드 라이팅, 선명한 윤곽선, 기술적 정밀함이 드러나는 촬영',
              english: 'Structural detail emphasis, hard lighting, crisp edges, technical precision visible, industrial photography style, high contrast, no text or labels'
            },
            {
              korean: '자연광 아래 유기적 질감, 따뜻한 색온도, 친환경적 분위기, 부드러운 톤',
              english: 'Natural daylight, organic texture, warm color temperature, eco-friendly atmosphere, soft tones, lifestyle product photography, no text overlay'
            },
            {
              korean: '초정밀 스티칭 디테일, 링 라이트 조명, 완벽한 대칭, 장인정신이 느껴지는 촬영',
              english: 'Ultra-precise stitching detail, ring light illumination, perfect symmetry, craftsmanship visible, artisan quality photography, no text or watermarks'
            },
            {
              korean: '프리미엄 패키징과 함께, 톱뷰 플랫레이, 균형잡힌 배치, 럭셔리 브랜드 감성',
              english: 'With premium packaging, top-view flatlay, balanced arrangement, luxury brand aesthetic, editorial product photography, no text elements'
            },
            {
              korean: '광택 표면 반사, 스펙큘러 하이라이트, 고급스러운 광택감, 완벽한 표면 처리',
              english: 'Glossy surface reflection, specular highlights, premium sheen, perfect surface treatment, high-end product photography, no text overlay'
            },
            {
              korean: '360도 회전 뷰 중 최적 각도, 모든 디테일이 보이는 완벽한 구도, 무결점 마감',
              english: '360-degree optimal angle, all details visible, perfect composition, flawless finish, comprehensive product view, professional photography, no text'
            }
          ]
        },
        {
          id: 2,
          name: '기능/편의성',
          icon: 'las la-cogs',
          description: '실용적 기능과 사용 편의성 강조',
          category: 'function',
          concepts: [
            {
              korean: '핵심 기능 부위 클로즈업, 선명한 포커스, 기능적 디테일이 명확히 보이는 촬영',
              english: 'Core function area close-up, sharp focus on functional details, clear visibility, technical product photography, no text annotations'
            },
            {
              korean: '사용 상태 촬영, 자연스러운 손 포지션, 실제 작동 모습, 클린한 배경',
              english: 'In-use state photography, natural hand position, actual operation visible, clean background, lifestyle product shot, no text overlay'
            },
            {
              korean: '다양한 활용 예시, 멀티 컷 구성, 각 용도별 최적 각도, 일관된 조명',
              english: 'Various usage examples, multi-shot composition, optimal angle for each purpose, consistent lighting, versatile product photography, no text'
            },
            {
              korean: '접힌 상태와 펼친 상태 비교, 컴팩트함 강조, 깔끔한 대칭 구도',
              english: 'Folded and unfolded state comparison, compactness emphasis, clean symmetrical composition, portability showcase, no text elements'
            },
            {
              korean: '조작 부위 확대, 버튼이나 스위치 디테일, 직관적 인터페이스가 보이는 촬영',
              english: 'Control area magnification, button or switch detail, intuitive interface visible, user-friendly design photography, no text labels'
            },
            {
              korean: '사용 전후 비교 컷, 동일 조명과 각도, 효과가 명확히 드러나는 구도',
              english: 'Before and after comparison shots, identical lighting and angle, effect clearly visible, demonstration photography, no text overlay'
            },
            {
              korean: '여러 모드나 설정 상태, 각 기능별 독립 촬영, 체계적 배열',
              english: 'Multiple modes or settings, each function photographed independently, systematic arrangement, feature showcase, no text descriptions'
            },
            {
              korean: '분리 가능 부품들, 플랫레이 배치, 청소나 관리 용이성 표현',
              english: 'Detachable parts, flatlay arrangement, easy cleaning and maintenance visible, modular design photography, no text'
            },
            {
              korean: '손에 쥔 모습, 그립감 강조, 인체공학적 곡선이 보이는 각도',
              english: 'Held in hand, grip comfort emphasis, ergonomic curves visible angle, human-centered design photography, no text overlay'
            },
            {
              korean: '스마트 기능 작동 상태, LED나 디스플레이 표시, 첨단 기술감이 느껴지는 촬영',
              english: 'Smart function operating state, LED or display visible, high-tech feel, modern technology photography, no text annotations'
            }
          ]
        },
        {
          id: 3,
          name: '디자인/미학',
          icon: 'las la-palette',
          description: '세련된 디자인과 심미성 강조',
          category: 'design',
          concepts: [
            {
              korean: '미니멀 무채색 배경, 상품 실루엣 강조, 클린한 라인, 네거티브 스페이스 극대화',
              english: 'Minimal achromatic background, product silhouette emphasis, clean lines, negative space maximized, modern design photography, no text'
            },
            {
              korean: '예술적 앵글, 황금비율 구도, 조형미 부각, 갤러리 전시 스타일',
              english: 'Artistic angle, golden ratio composition, sculptural beauty highlighted, gallery exhibition style, fine art product photography, no text'
            },
            {
              korean: '트렌디 컬러 배경, 보색 대비, 현대적 팔레트, 패션 에디토리얼 감성',
              english: 'Trendy color background, complementary color contrast, contemporary palette, fashion editorial sensibility, stylish photography, no text overlay'
            },
            {
              korean: '완벽한 대칭 구도, 기하학적 배치, 수학적 정밀함, 질서정연한 아름다움',
              english: 'Perfect symmetrical composition, geometric arrangement, mathematical precision, orderly beauty, architectural photography style, no text'
            },
            {
              korean: '극적인 명암 대비, 렘브란트 조명, 깊은 그림자, 입체적 볼륨감',
              english: 'Dramatic chiaroscuro, Rembrandt lighting, deep shadows, three-dimensional volume, cinematic product photography, no text elements'
            },
            {
              korean: '어워드 수상작 스타일, 완벽한 조명 셋업, 프리미엄 퀄리티, 전시회급 촬영',
              english: 'Award-winning style, perfect lighting setup, premium quality, exhibition-grade photography, design excellence, no text or watermarks'
            },
            {
              korean: '모던 인테리어 소품과 조화, 라이프스타일 컨텍스트, 세련된 공간 연출',
              english: 'Harmonized with modern interior props, lifestyle context, sophisticated space styling, editorial home photography, no text overlay'
            },
            {
              korean: '시그니처 패턴 극세밀 촬영, 텍스처 디테일, 독창적 디자인 요소 강조',
              english: 'Signature pattern extreme close-up, texture detail, unique design element emphasis, macro design photography, no text'
            },
            {
              korean: '컬러 배리에이션 일렬 배치, 동일 조명과 각도, 체계적 옵션 전시',
              english: 'Color variations in row arrangement, identical lighting and angle, systematic option display, catalog photography, no text labels'
            },
            {
              korean: '브랜드 시그니처 스타일, 일관된 무드와 톤, 아이덴티티가 명확한 촬영',
              english: 'Brand signature style, consistent mood and tone, clear identity, brand DNA photography, distinctive aesthetic, no text elements'
            }
          ]
        },
        {
          id: 4,
          name: '가성비/경제성',
          icon: 'las la-won-sign',
          description: '합리적 가격과 경제적 가치 강조',
          category: 'value',
          concepts: [
            {
              korean: '대용량 사이즈 강조, 스케일 비교 오브젝트 배치, 풍부한 내용물 표현',
              english: 'Large capacity size emphasis, scale comparison object placement, abundant contents visible, value photography, no text annotations'
            },
            {
              korean: '세트 구성 플랫레이, 모든 구성품 일목요연 배치, 체계적 정렬',
              english: 'Set configuration flatlay, all components clearly arranged, systematic alignment, bundle value photography, no text overlay'
            },
            {
              korean: '견고한 구조 클로즈업, 내구성 있는 소재 질감, 장기 사용 가능성 표현',
              english: 'Solid structure close-up, durable material texture, long-term usability visible, quality construction photography, no text'
            },
            {
              korean: '다용도 활용 멀티 컷, 각 용도별 촬영, 올인원 가치 표현',
              english: 'Multi-purpose usage multi-shot, each use case photographed, all-in-one value visible, versatile product photography, no text'
            },
            {
              korean: '가족 단위 사용 장면, 여러 사람이 함께 사용, 공유 가치 표현',
              english: 'Family unit usage scene, multiple people using together, shared value visible, family-friendly photography, no text elements'
            },
            {
              korean: '리필 가능 구조, 교체 가능 부품, 지속 가능성 강조',
              english: 'Refillable structure, replaceable parts, sustainability emphasis, eco-friendly design photography, no text overlay'
            },
            {
              korean: '세트 전체와 개별 제품 비교 구도, 번들 가치 시각화',
              english: 'Full set and individual product comparison composition, bundle value visualization, package deal photography, no text'
            },
            {
              korean: '심플한 디자인, 추가 액세서리 불필요, 완결성 있는 구성',
              english: 'Simple design, no additional accessories needed, complete configuration, all-inclusive photography, no text labels'
            },
            {
              korean: '프리미엄 퀄리티 디테일, 합리적 가격대 제품의 고급스러운 마감',
              english: 'Premium quality detail, high-end finish of reasonably priced product, accessible luxury photography, no text'
            },
            {
              korean: '실용적 기능 총집합, 모든 특징이 한눈에, 가치 극대화 표현',
              english: 'Practical features compilation, all characteristics at a glance, value maximization visible, comprehensive photography, no text overlay'
            }
          ]
        },
        {
          id: 5,
          name: '안전성/신뢰',
          icon: 'las la-shield-alt',
          description: '안전성과 브랜드 신뢰도 강조',
          category: 'safety',
          concepts: [
            {
              korean: '안전 인증 마크 클로즈업, 공식 승인 씰, 신뢰성 있는 인증서 디테일',
              english: 'Safety certification mark close-up, official approval seal, trustworthy certificate detail, compliance photography, no text overlay except certifications'
            },
            {
              korean: '천연 소재 질감, 유기농 원료, 친환경 성분의 자연스러운 색감',
              english: 'Natural material texture, organic ingredients, eco-friendly components natural color, safe product photography, no text'
            },
            {
              korean: '부드러운 표면 촬영, 저자극 소재 강조, 순한 느낌의 조명',
              english: 'Soft surface photography, hypoallergenic material emphasis, gentle feel lighting, family-safe product shot, no text elements'
            },
            {
              korean: '품질 검사 통과 제품, 완벽한 마감, 무결점 표면, 정밀 검수 흔적',
              english: 'Quality inspection passed product, perfect finish, flawless surface, precision inspection visible, quality control photography, no text'
            },
            {
              korean: '전통적 제조 방식, 장인의 손길, 오랜 노하우가 담긴 디테일',
              english: 'Traditional manufacturing method, artisan touch, long-standing know-how in details, heritage quality photography, no text overlay'
            },
            {
              korean: '전문가 사용 장면, 프로페셔널 환경, 신뢰할 수 있는 선택',
              english: 'Expert usage scene, professional environment, trustworthy choice, professional endorsement photography, no text'
            },
            {
              korean: '안전 설계 디테일, 보호 기능 부위, 사고 방지 구조 강조',
              english: 'Safety design detail, protective function area, accident prevention structure emphasis, secure construction photography, no text labels'
            },
            {
              korean: '프리미엄 패키징, 품질 보증 씰, 정품 인증 요소',
              english: 'Premium packaging, quality guarantee seal, authentic product certification elements, warranty photography, no text except seals'
            },
            {
              korean: '국제 표준 준수, 글로벌 인증 마크, 세계적 품질 기준',
              english: 'International standard compliance, global certification marks, world-class quality standards, regulatory photography, no text overlay except marks'
            },
            {
              korean: '실사용 흔적, 검증된 내구성, 장기 사용 후 상태 양호',
              english: 'Real usage traces, verified durability, good condition after long-term use, proven quality photography, no text'
            }
          ]
        },
        {
          id: 6,
          name: '차별성/독창성',
          icon: 'las la-lightbulb',
          description: '경쟁사 대비 차별화 포인트 강조',
          category: 'uniqueness',
          concepts: [
            {
              korean: '특허 기술 부위 클로즈업, 혁신적 메커니즘, 독창적 구조 디테일',
              english: 'Patented technology area close-up, innovative mechanism, unique structure detail, pioneering feature photography, no text except patent marks'
            },
            {
              korean: '시그니처 디자인 요소, 차별화된 형태, 독특한 실루엣 강조',
              english: 'Signature design element, differentiated form, unique silhouette emphasis, distinctive feature photography, no text overlay'
            },
            {
              korean: '문제 해결 과정 시각화, 혁신적 솔루션, 창의적 접근 방식',
              english: 'Problem-solving process visualization, innovative solution, creative approach, game-changing idea photography, no text'
            },
            {
              korean: '최고 성능 디스플레이, 우수한 스펙 표현, 업계 선도 기술',
              english: 'Top performance display, superior specs visible, industry-leading technology, benchmark photography, no text annotations'
            },
            {
              korean: '독자 기술 핵심 부품, 자체 개발 요소, 고유 공법의 결과물',
              english: 'Proprietary technology core component, in-house developed element, unique manufacturing result, exclusive method photography, no text'
            },
            {
              korean: '창의적 활용 장면, 비전통적 사용법, 새로운 관점의 응용',
              english: 'Creative usage scene, unconventional use method, fresh perspective application, innovative use case photography, no text'
            },
            {
              korean: '한정판 넘버링, 특별 에디션 마크, 희소성 표현 요소',
              english: 'Limited edition numbering, special edition mark, scarcity expression elements, collectible photography, no text except edition marks'
            },
            {
              korean: '콜라보 시그니처, 디자이너 각인, 프리미엄 파트너십 표식',
              english: 'Collaboration signature, designer engraving, premium partnership mark, exclusive cooperation photography, no text except signatures'
            },
            {
              korean: '수작업 디테일, 장인의 손길 흔적, 정성스러운 마감',
              english: 'Handcrafted detail, artisan touch traces, meticulous finishing, master craftsmanship photography, no text overlay'
            },
            {
              korean: '커스터마이징 옵션 전시, 개인화 가능 부분, 맞춤 제작 요소',
              english: 'Customization options display, personalizable parts, bespoke elements, made-to-order photography, no text labels'
            }
          ]
        },
        {
          id: 7,
          name: '라이프스타일',
          icon: 'las la-heart',
          description: '실생활 사용 장면과 감성 강조',
          category: 'lifestyle',
          concepts: [
            {
              korean: '아침 자연광 침실, 부드러운 햇살, 상품과 함께하는 모닝 루틴, 따뜻한 분위기',
              english: 'Morning natural light bedroom, soft sunlight, product with morning routine, warm atmosphere, lifestyle photography, no text overlay'
            },
            {
              korean: '감성 카페 인테리어, 따뜻한 앰비언트 조명, 상품과 커피, 여유로운 순간',
              english: 'Aesthetic cafe interior, warm ambient lighting, product with coffee, relaxed moment, Instagram-worthy photography, no text'
            },
            {
              korean: '야외 자연 배경, 신선한 공기, 활동적 장면, 상품 실사용 모습',
              english: 'Outdoor natural background, fresh air, active scene, product in real use, nature lifestyle photography, no text elements'
            },
            {
              korean: '홈 인테리어, 편안한 소파나 침대, 일상적 사용, 자연스러운 배치',
              english: 'Home interior, comfortable sofa or bed, everyday use, natural placement, domestic lifestyle photography, no text'
            },
            {
              korean: '친구들과 함께, 소셜 모임, 공유하는 즐거움, 그룹 활동',
              english: 'With friends, social gathering, shared enjoyment, group activity, social lifestyle photography, no text overlay'
            },
            {
              korean: '운동 장면, 피트니스 환경, 건강한 라이프스타일, 액티브 웨어와 조화',
              english: 'Exercise scene, fitness environment, healthy lifestyle, harmonized with activewear, wellness photography, no text'
            },
            {
              korean: '여행 가방 속, 휴가지 배경, 레저 활동, 여행 필수품으로서의 상품',
              english: 'In travel bag, vacation destination background, leisure activity, product as travel essential, holiday photography, no text'
            },
            {
              korean: '작업 공간, 깔끔한 책상, 생산성 도구들과 함께, 집중된 환경',
              english: 'Workspace, clean desk, with productivity tools, focused environment, work lifestyle photography, no text labels'
            },
            {
              korean: '가족 단위 활동, 따뜻한 가정 분위기, 함께하는 시간, 행복한 순간',
              english: 'Family unit activity, warm home atmosphere, time together, happy moments, family lifestyle photography, no text'
            },
            {
              korean: '개인 취미 공간, 여가 활동, 나만의 시간, 힐링 순간',
              english: 'Personal hobby space, leisure activity, me-time, healing moment, self-care lifestyle photography, no text overlay'
            }
          ]
        },
        {
          id: 8,
          name: '감성/스토리',
          icon: 'las la-book-open',
          description: '브랜드 스토리와 감성적 가치 전달',
          category: 'emotion',
          concepts: [
            {
              korean: '브랜드 헤리티지 표현, 역사적 요소, 철학이 담긴 디테일, 의미 있는 배경',
              english: 'Brand heritage expression, historical elements, philosophy in details, meaningful background, storytelling photography, no text except brand marks'
            },
            {
              korean: '장인의 손 클로즈업, 수작업 과정, 정성스러운 제작, 인간적 터치',
              english: 'Artisan hands close-up, handcrafting process, heartfelt production, human touch, craftsmanship photography, no text overlay'
            },
            {
              korean: '특별한 순간 연출, 감성적 조명, 의미 있는 장면, 추억 만들기',
              english: 'Special moment staging, emotional lighting, meaningful scene, memory making, sentimental photography, no text'
            },
            {
              korean: '친환경 소재, 지속가능 패키징, 자연과 조화, 책임감 있는 선택',
              english: 'Eco-friendly materials, sustainable packaging, harmony with nature, responsible choice, environmental photography, no text labels'
            },
            {
              korean: '전통적 요소, 시대를 초월한 디자인, 헤리티지 퀄리티, 영속성',
              english: 'Traditional elements, timeless design, heritage quality, lasting legacy, generational photography, no text'
            },
            {
              korean: '성장 스토리 시각화, 진화하는 디자인, 함께한 여정, 발전 과정',
              english: 'Growth story visualization, evolving design, shared journey, development process, brand evolution photography, no text overlay'
            },
            {
              korean: '선물 포장, 리본과 함께, 정성스러운 프레젠테이션, 마음 전달',
              english: 'Gift wrapping, with ribbon, thoughtful presentation, heart delivery, gift photography, no text except gift tags'
            },
            {
              korean: '일상의 작은 기쁨, 소소한 행복, 미소 짓게 하는 순간, 삶의 질 향상',
              english: 'Small joys of daily life, little happiness, smile-inducing moments, life quality enhancement, everyday delight photography, no text'
            },
            {
              korean: '희망적 이미지, 밝은 미래, 긍정적 메시지, 영감을 주는 비전',
              english: 'Hopeful imagery, bright future, positive message, inspiring vision, aspirational photography, no text overlay'
            },
            {
              korean: '진정성 있는 표현, 공감 가는 장면, 따뜻한 소통, 진심 전달',
              english: 'Authentic expression, relatable scene, warm communication, sincerity delivery, genuine connection photography, no text'
            }
          ]
        }
      ]
    };
  },
  computed: {
    isProcessDisabled() {
      return this.isLoading || !this.prompt || !this.hasGeminiApiKey;
    },
    hasGeminiApiKey() {
      return !!this.$store.state.settings[0]?.hasGeminiKey;
    },
    hasValidImage() {
      if (this.inputMethod === 'result') {
        return !!this.editedImage;
      }
      return this.originalImages.length > 0;
    },
    hasSourceImages() {
      // 컨셉 중 하나라도 sourceImageData가 있으면 수정 모드
      return this.concepts.some(concept => concept.sourceImageData);
    },
    currentPresets() {
      // 여러장 생성 모드일 때는 전용 프리셋 사용
      if (this.isMultipleImageMode) {
        return this.multipleGeneratePresets;
      }
      // 일반 모드일 때는 기존 로직
      return this.hasValidImage ? this.editPresets : this.generatePresets;
    },
    promptPlaceholder() {
      return '생성할 이미지 또는 편집할 이미지 내용을 자세히 설명해주세요. (Ctrl+V로 이미지 붙여넣기 가능)';
    },
    canSavePreset() {
      return this.newPreset.name.trim() && this.newPreset.prompt.trim() && this.userPresets.length < 10;
    }
  },

  async mounted() {
    await this.loadUserPresets();
    // 저장된 커스텀 프리셋 로드
    this.loadCustomPresets();
  },

  methods: {
    setInputMethod(method) {
      this.inputMethod = method;
      this.error = null;
      if (method === 'result' && this.editedImage) {
        this.originalImages = [this.editedImage.imageUrl];
        this.originalImage = this.editedImage.imageUrl; // 기존 호환성 유지
      } else if (method === 'paste') {
        // 붙여넣기 모드로 전환 시 포커스
        this.$nextTick(() => {
          if (this.$refs.pasteArea) {
            this.$refs.pasteArea.focus();
          }
        });
      }
    },

    async handleFileChange(event) {
      const files = event.target.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          await this.processImageFile(files[i]);
        }
      }
    },

    // 이미지 제거 메서드
    removeImage(index) {
      this.originalImages.splice(index, 1);
      // 기존 호환성을 위해 첫 번째 이미지를 originalImage로 설정
      this.originalImage = this.originalImages.length > 0 ? this.originalImages[0] : null;
    },

    async loadImageFromUrl() {
      if (!this.imageUrl) {
        this.error = '이미지 URL을 입력해주세요.';
        return;
      }

      // 중복 호출 방지 (blur와 click 이벤트가 동시에 발생하는 경우)
      if (this.isLoadingUrl) {
        return;
      }
      this.isLoadingUrl = true;

      try {
        // 1688 링크인지 확인
        const is1688Link = this.imageUrl.includes('detail.1688.com');

        if (is1688Link) {
          // 1688 상세페이지 이미지 처리
          await this.load1688DetailImages();
        } else {
          // 일반 URL 처리
          await this.loadRegularImageUrls();
        }

      } catch (error) {
        this.error = '이미지 URL 로드 실패: ' + error.message;
        this.originalImages = [];
        this.originalImage = null;
      } finally {
        this.isLoadingUrl = false;
      }
    },

    // 일반 URL에서 이미지 로드
    async loadRegularImageUrls() {
      // 여러 URL을 줄바꿈으로 분리
      const urls = this.imageUrl.split('\n').map(url => url.trim()).filter(url => url);
      this.originalImages = [];

      for (const url of urls) {
        // URL 유효성 검사
        new URL(url);

        // 서버 프록시를 통해 이미지 가져오기 (CORS 우회)
        const response = await fetch('/api/ai/proxy-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            imageUrl: url
          })
        });

        const result = await response.json();

        if (result.success) {
          this.originalImages.push(result.base64Data);
        } else {
          throw new Error(result.error);
        }
      }

      // 기존 호환성을 위해 첫 번째 이미지를 originalImage로 설정
      this.originalImage = this.originalImages.length > 0 ? this.originalImages[0] : null;
      this.error = null;
    },

    // 1688 상세페이지 이미지 로드 및 한글화 프리셋 자동 적용
    async load1688DetailImages() {
      this.isAnalyzing = true;
      this.error = null;

      try {
        // 1688 상품 정보 가져오기
        const productNo = this.imageUrl.slice(
          this.imageUrl.search('offer/') + 6,
          this.imageUrl.search('.html')
        );

        const response = await fetch('/api/aiPage/getProductInfo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify([{ productId: parseInt(productNo) }])
        });

        const res = await response.json();
        const data = res.result[0].result.result;

        // description에서 이미지 추출
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.description, 'text/html');
        const imgElements = doc.querySelectorAll('img');

        // 이미지 로드 및 필터링 (중복 제거)
        const imagePromises = Array.from(imgElements).map(img => this.loadImage(img.src));
        const results = await Promise.all(imagePromises);
        const validResults = results.filter(src => src !== null);

        // 중복 URL 제거 (완전 동일한 URL만 제거)
        const imageSources = [...new Set(validResults)];

        if (imageSources.length === 0) {
          throw new Error('상세페이지에서 이미지를 찾을 수 없습니다.');
        }

        // 이미지들을 프록시를 통해 base64로 변환
        this.originalImages = [];
        for (const imgSrc of imageSources) {
          const proxyResponse = await fetch('/api/ai/proxy-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageUrl: imgSrc
            })
          });

          const proxyResult = await proxyResponse.json();
          if (proxyResult.success) {
            this.originalImages.push(proxyResult.base64Data);
          }
        }

        // 여러장 생성 모드로 자동 전환
        this.isMultipleImageMode = true;
        this.conceptCount = this.originalImages.length;

        // 각 이미지에 대한 한글화 컨셉 생성
        this.concepts = this.originalImages.map((imageData, index) => ({
          id: index + 1,
          korean: `이미지 ${index + 1}: 중국어 텍스트를 자연스러운 한국어 카피라이팅으로 변경. 원본 이미지의 레이아웃과 디자인은 최대한 유지하되, 모든 중국어 텍스트를 한국 소비자에게 어필할 수 있는 감성적이고 설득력 있는 한국어로 번역. 폰트, 색상, 배치는 원본과 유사하게 유지.`,
          english: `Image ${index + 1}: Replace all Chinese text with natural Korean copywriting. Maintain the original layout and design as much as possible, but translate all Chinese text into emotional and persuasive Korean that appeals to Korean consumers. Keep fonts, colors, and placement similar to the original.`,
          imageUrl: null,
          isGenerating: false,
          isTranslating: false,
          promptModified: false,
          regenerateFromCurrent: false,
          regenerateModel: this.selectedModel,
          regenerateAspectRatio: this.aspectRatio,
          regenerateImageSize: this.imageSize,
          originalKorean: `이미지 ${index + 1}: 중국어 텍스트를 자연스러운 한국어 카피라이팅으로 변경`,
          koreanBeforeEdit: `이미지 ${index + 1}: 중국어 텍스트를 자연스러운 한국어 카피라이팅으로 변경`,
          sourceImageData: imageData // 원본 이미지 데이터 저장
        }));

        this.originalImage = this.originalImages.length > 0 ? this.originalImages[0] : null;
        this.error = null;

        // 성공 메시지
        this.$bvToast.toast(`${this.originalImages.length}개의 상세페이지 이미지를 불러왔습니다. 각 이미지를 한글화할 준비가 완료되었습니다.`, {
          title: '1688 이미지 로드 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

      } catch (error) {
        this.error = '1688 상품 정보 로드 실패: ' + error.message;
        this.originalImages = [];
        this.originalImage = null;
      } finally {
        this.isAnalyzing = false;
      }
    },

    // 이미지 로드 헬퍼 함수
    loadImage(src) {
      return new Promise((resolve) => {
        const image = new Image();
        image.src = src;
        image.onload = () => {
          if (image.naturalWidth > 50 && image.naturalHeight > 50) {
            resolve(image.src);
          } else {
            resolve(null);
          }
        };
        image.onerror = () => {
          resolve(null);
        };
      });
    },

    async handleProcess() {
      // 에이전틱 모드인 경우
      if (this.isMultipleImageMode) {
        await this.handleAgenticGeneration();
      } else {
        // 일반 모드
        if (this.hasValidImage) {
          await this.handleEdit();
        } else {
          await this.handleGenerate();
        }
      }
    },

    // 에이전틱 이미지 생성 플로우
    async handleAgenticGeneration() {
      if (!this.prompt) {
        this.error = '생성할 이미지에 대한 설명을 입력해주세요.';
        return;
      }

      this.isAnalyzing = true;
      this.error = null;
      this.concepts = [];

      try {
        // 1. 프롬프트 분석 및 컨셉 생성
        const response = await fetch('/api/ai/analyze-prompt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt: this.prompt,
            conceptCount: this.conceptCount // 사용자가 선택한 개수
          })
        });

        const result = await response.json();

        if (result.success) {
          // 컨셉 데이터 초기화
          this.concepts = result.concepts.map(concept => ({
            ...concept,
            imageUrl: null,
            isGenerating: false,
            isTranslating: false, // 번역 중 상태
            error: null,
            koreanEdited: false,
            koreanBeforeEdit: concept.korean, // 편집 전 한글 프롬프트
            promptModified: false, // 프롬프트 수정 여부
            regenerateFromCurrent: false, // 재생성 모드 (false: 새로 생성, true: 현재 이미지 편집)
            regenerateModel: this.selectedModel, // 재생성 시 사용할 모델
            regenerateAspectRatio: this.aspectRatio, // 재생성 시 사용할 종횡비
            regenerateImageSize: this.imageSize, // 재생성 시 사용할 해상도
            originalKorean: concept.korean // 원본 한글 프롬프트 저장
          }));
        } else {
          this.error = result.error || '프롬프트 분석에 실패했습니다.';
        }
      } catch (err) {
        this.error = err.message || '네트워크 오류가 발생했습니다.';
      } finally {
        this.isAnalyzing = false;
      }
    },

    // 한글 프롬프트 수정 시 영문 프롬프트 재생성
    async onKoreanPromptEdit(concept) {
      if (!concept.korean || concept.korean.trim() === '') {
        return;
      }

      // 실제로 수정되었는지 확인 (blur 시 무조건 실행되지 않도록)
      if (concept.koreanBeforeEdit === concept.korean) {
        return;
      }

      // 이미지가 생성된 후 프롬프트가 수정되었는지 확인
      if (concept.imageUrl && concept.originalKorean !== concept.korean) {
        concept.promptModified = true;
      } else {
        concept.promptModified = false;
      }

      concept.koreanEdited = true;
      concept.isTranslating = true; // 번역 중 상태 시작

      try {
        const response = await fetch('/api/ai/translate-prompt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            koreanPrompt: concept.korean
          })
        });

        const result = await response.json();

        if (result.success) {
          concept.english = result.englishPrompt;
        } else {
          console.error('프롬프트 번역 실패:', result.error);
        }
      } catch (err) {
        console.error('프롬프트 번역 오류:', err);
      } finally {
        concept.isTranslating = false; // 번역 중 상태 종료
      }
    },

    // 이미지 재생성
    async regenerateConceptImage(concept) {
      concept.isGenerating = true;
      concept.error = null;

      try {
        const requestBody = {
          prompt: concept.english,
          model: concept.regenerateModel // 컨셉별 모델 사용
        };

        // Nano Banana Pro 모델인 경우에만 해상도 및 종횡비 추가
        if (concept.regenerateModel === 'gemini-3-pro-image-preview') {
          if (concept.regenerateAspectRatio) {
            requestBody.aspectRatio = concept.regenerateAspectRatio;
          }
          requestBody.imageSize = concept.regenerateImageSize;
        }

        let endpoint = '/api/ai/generate-image';

        // 재생성 모드에 따라 엔드포인트 및 데이터 설정
        if (concept.regenerateFromCurrent) {
          // 현재 이미지에서 편집
          endpoint = '/api/ai/edit-image';
          requestBody.imageData = [concept.imageUrl];
        } else {
          // 새로 생성 - 원본 이미지가 있으면 사용
          const hasImage = this.hasValidImage;
          if (hasImage) {
            endpoint = '/api/ai/edit-image';
            const imagesToEdit = this.getImageToEdit();
            requestBody.imageData = imagesToEdit;
          }
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        console.log(response)
        const result = await response.json();

        if (result.success) {
          concept.imageUrl = result.imageUrl;
          concept.promptModified = false; // 재생성 완료 후 수정 플래그 초기화
          concept.originalKorean = concept.korean; // 현재 프롬프트를 원본으로 저장
          concept.koreanBeforeEdit = concept.korean; // 편집 전 상태도 업데이트
        } else {
          concept.error = result.error || '이미지 재생성에 실패했습니다.';
        }
      } catch (err) {
        concept.error = err.message || '네트워크 오류가 발생했습니다.';
      } finally {
        concept.isGenerating = false;
      }
    },

    // 개별 컨셉 이미지 생성
    async generateConceptImage(concept) {
      concept.isGenerating = true;
      concept.error = null;

      try {
        const requestBody = {
          prompt: concept.english,
          model: this.selectedModel
        };

        // Nano Banana Pro 모델인 경우에만 해상도 및 종횡비 추가
        if (this.selectedModel === 'gemini-3-pro-image-preview') {
          if (this.aspectRatio) {
            requestBody.aspectRatio = this.aspectRatio;
          }
          requestBody.imageSize = this.imageSize;
        }

        // 이미지가 있으면 편집 모드, 없으면 생성 모드
        let endpoint = '/api/ai/generate-image';

        // 1688 이미지 한글화의 경우: 각 컨셉의 sourceImageData 사용
        if (concept.sourceImageData) {
          endpoint = '/api/ai/edit-image';
          requestBody.imageData = [concept.sourceImageData];
        }
        // 일반 이미지 편집의 경우
        else if (this.hasValidImage) {
          endpoint = '/api/ai/edit-image';
          const imagesToEdit = this.getImageToEdit();
          requestBody.imageData = imagesToEdit;
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (result.success) {
          concept.imageUrl = result.imageUrl;
          concept.originalKorean = concept.korean; // 이미지 생성 후 현재 프롬프트를 원본으로 저장
          concept.koreanBeforeEdit = concept.korean; // 편집 전 상태도 업데이트
          concept.promptModified = false; // 수정 플래그 초기화
        } else {
          concept.error = result.error || '이미지 생성에 실패했습니다.';
        }
      } catch (err) {
        concept.error = err.message || '네트워크 오류가 발생했습니다.';
      } finally {
        concept.isGenerating = false;
      }
    },

    // 모든 컨셉 이미지 동시 생성
    async generateAllConcepts() {
      this.isGeneratingAll = true;

      // 모든 컨셉을 동시에 생성
      const promises = this.concepts.map(concept => {
        if (!concept.imageUrl && !concept.isGenerating) {
          return this.generateConceptImage(concept);
        }
        return Promise.resolve();
      });

      await Promise.all(promises);

      this.isGeneratingAll = false;
    },

    // 컨셉 이미지 다운로드
    downloadConceptImage(concept, index) {
      if (!concept.imageUrl) return;

      const link = document.createElement('a');
      link.href = concept.imageUrl;
      link.download = `concept-${index + 1}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async handleGenerate() {
      if (!this.prompt) {
        this.error = '생성할 이미지에 대한 설명을 입력해주세요.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.editedImage = null;

      try {
        const requestBody = {
          prompt: this.prompt,
          model: this.selectedModel
        };

        // Nano Banana Pro 모델인 경우에만 해상도 및 종횡비 추가
        if (this.selectedModel === 'gemini-3-pro-image-preview') {
          if (this.aspectRatio) {
            requestBody.aspectRatio = this.aspectRatio;
          }
          requestBody.imageSize = this.imageSize;
        }

        const response = await fetch('/api/ai/generate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (result.success) {
          this.editedImage = {
            imageUrl: result.imageUrl,
            text: result.text
          };
        } else {
          this.handleApiError(result.error);
        }
      } catch (err) {
        this.error = err.message || '네트워크 오류가 발생했습니다.';
      } finally {
        this.isLoading = false;
      }
    },

    async handleEdit() {
      const imagesToEdit = this.getImageToEdit();

      if (!this.prompt || !imagesToEdit || imagesToEdit.length === 0) {
        this.error = '이미지를 선택하고 편집 내용을 입력해주세요.';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const requestBody = {
          imageData: imagesToEdit, // 다중 이미지 전송
          prompt: this.prompt,
          model: this.selectedModel
        };

        // Nano Banana Pro 모델인 경우에만 해상도 및 종횡비 추가
        if (this.selectedModel === 'gemini-3-pro-image-preview') {
          if (this.aspectRatio) {
            requestBody.aspectRatio = this.aspectRatio;
          }
          requestBody.imageSize = this.imageSize;
        }

        const response = await fetch('/api/ai/edit-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (result.success) {
          this.editedImage = {
            imageUrl: result.imageUrl,
            text: result.text
          };
        } else {
          this.handleApiError(result.error);
        }
      } catch (err) {
        this.error = err.message || '네트워크 오류가 발생했습니다.';
      } finally {
        this.isLoading = false;
      }
    },

    getImageToEdit() {
      if (this.inputMethod === 'result' && this.editedImage) {
        return [this.editedImage.imageUrl];
      }
      return this.originalImages;
    },

    handleApiError(error) {
      if (error && error.includes('API 키')) {
        this.error = error + ' 우측 상단 프로필 메뉴에서 "Gemini API 설정"을 확인해주세요.';
      } else {
        this.error = error || '처리 중 오류가 발생했습니다.';
      }
    },

    handleClear() {
      this.prompt = '';
      this.originalImages = [];
      this.originalImage = null;
      this.editedImage = null;
      this.imageUrl = '';
      this.error = null;
      this.isLoading = false;
      this.fileInputKey = Date.now(); // 파일 입력 리셋
      this.inputMethod = 'upload'; // 입력 방법 초기화
    },

    downloadImage() {
      if (!this.editedImage || !this.editedImage.imageUrl) {
        return;
      }

      try {
        // base64 데이터 URL에서 이미지 다운로드
        const link = document.createElement('a');
        link.href = this.editedImage.imageUrl;

        // 파일명 생성 (현재 시간 기반)
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
        link.download = `nanobanana-edited-${timestamp}.png`;

        // 임시로 DOM에 추가하고 클릭 후 제거
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('이미지 다운로드 오류:', error);
        alert('이미지 다운로드 중 오류가 발생했습니다.');
      }
    },

    async applyPreset(preset) {
      // 여러장 생성 모드이고 프리셋에 concepts가 있으면 바로 컨셉 생성
      if (this.isMultipleImageMode && preset.concepts) {
        // 이미지가 있으면 먼저 분석해서 프롬프트 최적화
        if (this.hasValidImage) {
          await this.analyzeImageAndOptimizePrompts(preset);
        } else {
          // 이미지가 없으면 기존 프리셋 그대로 사용
          this.concepts = preset.concepts.map((concept, index) => ({
            id: index + 1,
            korean: concept.korean,
            english: concept.english,
            imageUrl: null,
            isGenerating: false,
            isTranslating: false,
            promptModified: false,
            regenerateFromCurrent: false,
            regenerateModel: this.selectedModel,
            regenerateAspectRatio: this.aspectRatio,
            regenerateImageSize: this.imageSize,
            originalKorean: concept.korean,
            koreanBeforeEdit: concept.korean
          }));
        }
        this.prompt = ''; // 프롬프트는 비워둠
      } else {
        // 일반 모드는 기존대로
        this.prompt = preset.prompt;
      }
      this.error = null; // 에러 메시지 초기화
    },

    // 이미지 분석 후 프롬프트 최적화
    async analyzeImageAndOptimizePrompts(preset) {
      this.isAnalyzing = true;
      this.error = null;
      this.concepts = [];

      try {
        // 이미지 데이터 가져오기
        const imagesToAnalyze = this.getImageToEdit();

        // 서버에 이미지 분석 요청
        const response = await fetch('/api/ai/analyze-product-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageData: imagesToAnalyze[0], // 첫 번째 이미지만 분석
            presetCategory: preset.category,
            presetConcepts: preset.concepts
          })
        });

        const result = await response.json();

        if (result.success) {
          // 최적화된 컨셉으로 설정
          this.concepts = result.optimizedConcepts.map((concept, index) => ({
            id: index + 1,
            korean: concept.korean,
            english: concept.english,
            imageUrl: null,
            isGenerating: false,
            isTranslating: false,
            promptModified: false,
            regenerateFromCurrent: false,
            regenerateModel: this.selectedModel,
            regenerateAspectRatio: this.aspectRatio,
            regenerateImageSize: this.imageSize,
            originalKorean: concept.korean,
            koreanBeforeEdit: concept.korean
          }));
        } else {
          // 분석 실패 시 기존 프리셋 사용
          console.error('이미지 분석 실패:', result.error);
          this.concepts = preset.concepts.map((concept, index) => ({
            id: index + 1,
            korean: concept.korean,
            english: concept.english,
            imageUrl: null,
            isGenerating: false,
            isTranslating: false,
            promptModified: false,
            regenerateFromCurrent: false,
            regenerateModel: this.selectedModel,
            regenerateAspectRatio: this.aspectRatio,
            regenerateImageSize: this.imageSize,
            originalKorean: concept.korean,
            koreanBeforeEdit: concept.korean
          }));
        }
      } catch (err) {
        console.error('이미지 분석 오류:', err);
        // 오류 발생 시 기존 프리셋 사용
        this.concepts = preset.concepts.map((concept, index) => ({
          id: index + 1,
          korean: concept.korean,
          english: concept.english,
          imageUrl: null,
          isGenerating: false,
          isTranslating: false,
          promptModified: false,
          regenerateFromCurrent: false,
          regenerateModel: this.selectedModel,
          regenerateAspectRatio: this.aspectRatio,
          regenerateImageSize: this.imageSize,
          originalKorean: concept.korean,
          koreanBeforeEdit: concept.korean
        }));
      } finally {
        this.isAnalyzing = false;
      }
    },

    // 컨셉 삭제
    async removeConcept(index) {
      if (await this.confirmInPage(`컨셉 ${index + 1}을(를) 삭제하시겠습니까?`)) {
        this.concepts.splice(index, 1);
        // ID 재정렬
        this.concepts.forEach((concept, idx) => {
          concept.id = idx + 1;
        });
      }
    },

    // 현재 컨셉들을 프리셋으로 저장
    async saveAsPreset() {
      if (this.concepts.length === 0) {
        alert('저장할 컨셉이 없습니다.');
        return;
      }

      const presetName = prompt('프리셋 이름을 입력하세요:');
      if (!presetName || !presetName.trim()) {
        return;
      }

      const presetIcon = prompt('아이콘 클래스를 입력하세요 (예: las la-star):', 'las la-star');
      const presetDescription = prompt('프리셋 설명을 입력하세요:', '사용자 정의 프리셋');

      const newPreset = {
        id: Date.now(), // 고유 ID
        name: presetName.trim(),
        icon: presetIcon || 'las la-star',
        description: presetDescription || '사용자 정의 프리셋',
        category: 'custom',
        isCustom: true,
        concepts: this.concepts.map(concept => ({
          korean: concept.korean,
          english: concept.english
        }))
      };

      // 로컬 스토리지에 저장
      try {
        const savedPresets = JSON.parse(localStorage.getItem('nanobanana_custom_presets') || '[]');
        savedPresets.push(newPreset);
        localStorage.setItem('nanobanana_custom_presets', JSON.stringify(savedPresets));

        // 현재 프리셋 목록에 추가
        this.multipleGeneratePresets.push(newPreset);

        alert('프리셋이 저장되었습니다!');
      } catch (error) {
        console.error('프리셋 저장 오류:', error);
        alert('프리셋 저장 중 오류가 발생했습니다.');
      }
    },

    // 저장된 커스텀 프리셋 로드
    loadCustomPresets() {
      try {
        const savedPresets = JSON.parse(localStorage.getItem('nanobanana_custom_presets') || '[]');
        // 기존 프리셋 목록에 커스텀 프리셋 추가
        this.multipleGeneratePresets = [...this.multipleGeneratePresets, ...savedPresets];
      } catch (error) {
        console.error('커스텀 프리셋 로드 오류:', error);
      }
    },

    // 붙여넣기 영역 포커스
    focusPasteArea() {
      if (this.$refs.pasteArea) {
        this.$refs.pasteArea.focus();
      }
    },

    // 프롬프트 영역에서 키보드 이벤트 처리
    handlePromptKeydown(event) {
      // Ctrl+Enter로 AI 실행
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        this.handleProcess();
      }
      // Enter만 누르면 기본 줄바꿈 동작 (preventDefault 하지 않음)
    },

    // 프롬프트 영역에서 붙여넣기 처리
    async handlePromptPaste(event) {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await this.processImageFile(file);
            // 붙여넣기 방법으로 자동 설정
            this.inputMethod = 'paste';
          }
        }
      }
    },

    // 붙여넣기 영역에서 붙여넣기 처리
    async handlePaste(event) {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await this.processImageFile(file);
          }
        }
      }
    },

    // 드래그 앤 드롭 처리
    async handleDrop(event) {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          await this.processImageFile(file);
        } else {
          this.error = '이미지 파일만 업로드 가능합니다.';
        }
      }
    },

    // 이미지 파일 처리 공통 함수
    async processImageFile(file) {
      if (!file.type.startsWith('image/')) {
        this.error = '올바른 이미지 파일을 선택해주세요.';
        return;
      }

      this.error = null;

      const reader = new FileReader();
      reader.onloadend = () => {
        this.originalImages.push(reader.result);
        // 기존 호환성을 위해 첫 번째 이미지를 originalImage로 설정
        if (this.originalImages.length === 1) {
          this.originalImage = reader.result;
        }
      };
      reader.readAsDataURL(file);
    },

    // IndexedDB 관련 메서드들
    async openDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(scopedDbName('NanoBananaDB'), 1);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('presets')) {
            db.createObjectStore('presets', { keyPath: 'id', autoIncrement: true });
          }
        };
      });
    },

    async loadUserPresets() {
      try {
        const db = await this.openDB();
        const transaction = db.transaction(['presets'], 'readonly');
        const store = transaction.objectStore('presets');
        const request = store.getAll();

        request.onsuccess = () => {
          this.userPresets = request.result || [];
        };
      } catch (error) {
        console.error('사용자 프리셋 로드 실패:', error);
      }
    },

    async saveUserPreset() {
      if (!this.canSavePreset) return;

      try {
        const db = await this.openDB();
        const transaction = db.transaction(['presets'], 'readwrite');
        const store = transaction.objectStore('presets');

        const preset = {
          name: this.newPreset.name.trim(),
          icon: this.newPreset.icon.trim() || 'las la-star',
          description: this.newPreset.description.trim(),
          prompt: this.newPreset.prompt.trim()
        };

        await store.add(preset);
        await this.loadUserPresets();
        this.closeAddPresetModal();
      } catch (error) {
        console.error('프리셋 저장 실패:', error);
        this.error = '프리셋 저장에 실패했습니다.';
      }
    },

    async deleteUserPreset(id) {
      if (!(await this.confirmInPage('이 프리셋을 삭제하시겠습니까?'))) return;

      try {
        const db = await this.openDB();
        const transaction = db.transaction(['presets'], 'readwrite');
        const store = transaction.objectStore('presets');

        await store.delete(id);
        await this.loadUserPresets();
      } catch (error) {
        console.error('프리셋 삭제 실패:', error);
        this.error = '프리셋 삭제에 실패했습니다.';
      }
    },

    openAddPresetModal() {
      this.newPreset.prompt = this.prompt; // 현재 프롬프트를 자동으로 채움
      this.showAddPresetModal = true;
    },

    selectIcon(iconClass) {
      this.newPreset.icon = iconClass;
    },

    closeAddPresetModal() {
      this.showAddPresetModal = false;
      this.newPreset = {
        name: '',
        icon: 'las la-star',
        description: '',
        prompt: ''
      };
    }
  }
};
</script>

<style scoped>
/* ========== 헤더 스타일 ========== */
.nano-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.header-icon-wrapper i {
  font-size: 2rem;
  color: white;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
}

/* ========== 카드 스타일 ========== */
.nano-control-card,
.nano-result-card {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

.nano-card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
  padding: 16px 24px;
  flex-shrink: 0;
}

.nano-header-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.nano-header-buttons .btn-sm {
  padding: 6px 16px;
  font-size: 0.85rem;
  font-weight: 600;
}

.nano-scrollable-content {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
}

.nano-scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.nano-scrollable-content::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 10px;
}

.nano-scrollable-content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
}

.nano-scrollable-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%);
}

/* ========== 섹션 스타일 ========== */
.nano-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.nano-section:hover {
  background: #f1f3f5;
}

.section-header {
  display: flex;
  align-items: center;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
}

.section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

/* ========== 프롬프트 입력 스타일 ========== */
.nano-textarea {
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 16px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  resize: vertical;
}

.nano-textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
  background: white;
}

.nano-hint {
  color: #6c757d;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

.concept-count-input {
  display: flex;
  align-items: center;
}

.concept-count-input input {
  width: 60px;
  border-radius: 8px;
  border: 2px solid #dee2e6;
  text-align: center;
  font-weight: 600;
}

.concept-count-input input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

/* ========== 모델 설정 스타일 ========== */
.model-settings-grid {
  display:flex;
  flex-direction: column;
}

.setting-item {
  display: flex;
  flex-direction: column;
}

.setting-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.nano-select {
  border: 2px solid #dee2e6;
  border-radius: 10px;
  
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.nano-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

/* ========== 탭 스타일 ========== */
.nano-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  background: white;
  padding: 7px;
  border-radius: 12px;
  border: 2px solid #e9ecef;
}

.nano-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5px 3px;
  border: none;
  background: transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: 500;
}

.nano-tab i {
  font-size: 1.5rem;
  margin-bottom: 2px;
}

.nano-tab:hover:not(:disabled) {
  background: #f8f9fa;
  color: #495057;
}

.nano-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.nano-tab:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ========== 파일 업로드 스타일 ========== */
.custom-file-upload {
  border: 3px dashed #dee2e6;
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.custom-file-upload:hover {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.upload-placeholder {
  color: #6c757d;
}

/* ========== 붙여넣기 영역 스타일 ========== */
.paste-area {
  border: 3px dashed #dee2e6;
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  outline: none;
}

.paste-area:hover,
.paste-area:focus {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.paste-placeholder {
  color: #6c757d;
}

.pasted-images {
  width: 100%;
}

/* ========== URL 입력 스타일 ========== */
.nano-url-input-group {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.nano-url-input-group textarea {
  flex: 1;
}

.nano-url-load-btn {
  min-width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.nano-url-load-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.nano-url-load-btn i {
  font-size: 1.5rem;
}

/* ========== 결과 재편집 스타일 ========== */
.nano-result-reuse {
  background: white;
  border-radius: 16px;
  padding: 20px;
  border: 2px solid #e9ecef;
}

.reuse-image-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
}

.reuse-image-wrapper img {
  max-height: 250px;
  width: 100%;
  object-fit: contain;
}

.reuse-info {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.9rem;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
}

/* ========== 모달 스타일 ========== */
.nano-modal-backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.nano-modal {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.nano-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: none;
  padding: 20px 24px;
}

.nano-modal-header .modal-title {
  color: white;
}

.nano-modal-header .close {
  color: white;
  opacity: 0.9;
  text-shadow: none;
}

.nano-modal-header .close:hover {
  opacity: 1;
}

.nano-modal-body {
  padding: 24px;
}

.nano-modal-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.nano-modal-input {
  border: 2px solid #dee2e6;
  border-radius: 10px;
  padding: 10px 14px;
  transition: all 0.3s ease;
}

.nano-modal-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

.nano-icon-selector {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.nano-icon-option {
  width: 100%;
  aspect-ratio: 1;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.nano-icon-option i {
  font-size: 1.5rem;
  color: #6c757d;
}

.nano-icon-option:hover {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
}

.nano-icon-option.active {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.nano-icon-option.active i {
  color: white;
}

.nano-modal-footer {
  border-top: 2px solid #f1f3f5;
  padding: 16px 24px;
  background: #f8f9fa;
}

.uploaded-image {
  max-height: 300px;
  overflow: hidden;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 10px;
}

.image-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.image-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 25px;
  height: 25px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0.8;
}

.remove-btn:hover {
  opacity: 1;
}

/* ========== 결과 표시 영역 스타일 ========== */
.result-container {
  min-height: 400px;
}

.nano-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 10;
  backdrop-filter: blur(5px);
}

.nano-result-wrapper {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.nano-image-container {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.nano-image-container:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  transform: translateY(-4px);
}

.nano-result-image {
  width: 100%;
  display: block;
  border-radius: 16px;
}

.nano-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.3) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nano-image-container:hover .nano-image-overlay {
  opacity: 1;
}

.nano-download-btn {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.nano-download-btn i {
  font-size: 1.75rem;
}

.nano-download-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.nano-result-info {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #e7f3ff 0%, #cfe7ff 100%);
  border-left: 4px solid #007bff;
  border-radius: 12px;
  color: #004085;
  font-size: 0.95rem;
}

.nano-result-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6c757d;
  text-align: center;
  padding: 40px;
}

.placeholder-icon-wrapper {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.placeholder-icon-wrapper i {
  font-size: 4rem;
  color: #adb5bd;
}

.placeholder-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.placeholder-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
}

.result-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #6c757d;
}

/* ========== 프리셋 스타일 ========== */
.preset-category-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.preset-item-wrapper {
  position: relative;
}

.nano-preset-btn {
  width: 100%;
  padding: 8px 6px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background: white;
  display: flex;
  justify-content: center;
  /* flex-direction: column; */
  align-items: center;
  gap: 3px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 500;
  color: #495057;
}

.nano-preset-btn i {
  font-size: 1.1rem;
}

.nano-preset-btn.default-preset:hover {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.nano-preset-btn.user-preset {
  border-color: #28a745;
}

.nano-preset-btn.user-preset:hover {
  border-color: #28a745;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
}

.preset-delete-btn {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  background: #dc3545;
  border: 2px solid white;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 10;
}

.preset-item-wrapper:hover .preset-delete-btn {
  opacity: 1;
}

.preset-delete-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

/* ========== 액션 버튼 스타일 ========== */
.nano-action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.nano-main-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  font-weight: 600;
}

.nano-main-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.nano-main-btn:disabled {
  background: #6c757d;
  opacity: 0.6;
}

.gap-2 {
  gap: 0.5rem;
}

.image-container {
  display: inline-block;
  width: 100%;
}

.download-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 20;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.download-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.download-btn i {
  font-size: 1.2rem;
}

/* 에이전틱 모드 스타일 */
.concepts-tree {
  max-height: 650px;
  overflow-y: auto;
  padding-right: 5px;
}

.concepts-tree::-webkit-scrollbar {
  width: 6px;
}

.concepts-tree::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.concepts-tree::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.concepts-tree::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.concept-item {
  position: relative;
}

.concept-card {
  border: none;
  border-left: 4px solid #007bff;
  transition: all 0.3s ease;
  overflow: hidden;
}

.concept-card:hover {
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.15);
  transform: translateY(-2px);
  border-left-color: #0056b3;
}

.concept-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 10px;
}

/* 컨셉 카드 좌우 레이아웃 */
.concept-content-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.concept-left {
  flex: 1;
  min-width: 0;
}

.concept-right {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.concept-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  color: #6c757d;
}

.concept-image-placeholder i {
  font-size: 3rem;
  margin-bottom: 10px;
}

.concept-image-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

/* 반응형 */
@media (max-width: 992px) {
  .concept-content-layout {
    flex-direction: column;
  }

  .concept-right {
    flex: 1;
    width: 100%;
  }
}

/* 컨셉 삭제 버튼 */
.concept-delete-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.concept-delete-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.concept-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

/* 프롬프트 영역 */
.prompts-section {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.prompt-label {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.prompt-input {
  font-size: 13px;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
  resize: none;
}

.prompt-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.english-prompt {
  background-color: #e9ecef;
  color: #6c757d;
  font-style: italic;
}

/* 재생성 섹션 */
.regenerate-section {
  background: linear-gradient(135deg, #fff5e6 0%, #ffe6cc 100%);
  padding: 15px;
  border-radius: 12px;
  border: 2px solid #ffc107;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
}

.regenerate-header {
  font-size: 14px;
  color: #856404;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ffc107;
}

.regenerate-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.option-card {
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.option-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.option-card.active {
  border-color: #007bff;
  background: linear-gradient(135deg, #e7f3ff 0%, #cfe7ff 100%);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.25);
}

.option-card label {
  cursor: pointer;
  padding: 0;
}

.option-card i {
  font-size: 18px;
  color: #007bff;
}

.regenerate-settings {
  background: white;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.setting-label {
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 4px;
}

/* 이미지 영역 */
.concept-image-wrapper {
  margin-top: 12px;
}

.concept-image-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.concept-image-container img {
  width: 100%;
  display: block;
  transition: transform 0.3s ease;
}

.concept-image-container:hover img {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.concept-image-container:hover .image-overlay {
  opacity: 1;
}

.image-action-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.image-action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.image-action-btn i {
  font-size: 1.2rem;
}

/* 생성 중 오버레이 */
.generating-overlay {
  background: rgba(255, 255, 255, 0.95);
  padding: 40px 20px;
  border-radius: 12px;
  border: 2px dashed #007bff;
}

.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.cursor-pointer {
  cursor: pointer;
}

.preset-buttons {
  margin-bottom: 1rem;
}

.preset-btn {
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preset-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preset-btn i {
  width: 16px;
  text-align: center;
}

.input-method-tabs .btn {
  font-size: 0.8rem;
  padding: 0.375rem 0.5rem;
}

.result-reuse {
  text-align: center;
  padding: 1rem;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
}

.input-group .form-control {
  border-right: 0;
}

.input-group-append .btn {
  border-left: 0;
}

.paste-area {
  min-height: 200px;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.paste-area:hover,
.paste-area:focus {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.paste-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
}

.pasted-image {
  padding: 1rem;
  text-align: center;
}

.pasted-image img {
  max-height: 200px;
}

.user-preset-btn {
  border-color: #28a745 !important;
  color: #28a745 !important;
}

.user-preset-btn:hover {
  background-color: #28a745 !important;
  color: white !important;
}

.preset-delete-btn {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  font-size: 10px;
  line-height: 1;
  display: none;
}

.position-relative:hover .preset-delete-btn {
  display: block;
}

.modal.show {
  display: block !important;
}

.icon-selector {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

.icon-option {
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.icon-option:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
}

.icon-option.active {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.icon-option.active:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}
</style>
